import { useEffect } from 'react';
import { authAPI } from './api';

// ── KMS session hand-off from the public site ────────────────────────────────
// The KMS lives on its own origin (e.g. insightsbeta.carlosartiles.com) with its
// own session cookie. My Account's "KMS" quick link seats that cookie by minting
// a one-time SSO token and hitting `<kms>/sso-login?token=…`. But the KMS links
// on the PUBLIC site — the Insights section's article cards and its "View all"
// category buttons — come from the blog feed as plain `<a href>`s straight to the
// KMS, carrying NO token. So an already-logged-in visitor who enters the KMS from
// the home page lands anonymously, while the same person entering from My Account
// stays logged in. This module closes that gap for EVERY KMS link on the site.

// The KMS origin, derived from settings (brand-agnostic). null when unset.
export function kmsOrigin(settings) {
  const base = settings && settings.kms_native_base;
  if (!base) return null;
  try { return new URL(base).origin; } catch { return null; }
}

let inFlight = false;

// Mint a token and hand off to `<origin>/sso-login?token=…&next=<path>` so the
// member lands on the exact page they clicked (the KMS honours `next`), now
// logged in. Falls back to plain navigation on any error so a link is never dead.
async function kmsSsoGo(origin, nextPath, openTab) {
  // For a new-tab link (the Insights cards are target="_blank"), open the tab
  // synchronously inside the click gesture so the popup blocker doesn't eat it;
  // the token mint that follows is async. Drop the opener for safety.
  let win = null;
  if (openTab) {
    try { win = window.open('', '_blank'); if (win) win.opener = null; } catch { win = null; }
  }
  const go = (dest) => {
    if (openTab) {
      if (win) win.location = dest;
      else window.open(dest, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = dest;
    }
  };
  if (inFlight) { if (win) win.close(); return; }
  inFlight = true;
  try {
    const res = await authAPI.generateSsoToken();
    const q = `token=${encodeURIComponent(res.data.sso_token)}` +
      (nextPath && nextPath !== '/' ? `&next=${encodeURIComponent(nextPath)}` : '');
    go(`${origin}/sso-login?${q}`);
  } catch {
    go(origin + (nextPath || '/'));
  } finally {
    inFlight = false;
  }
}

// Install a delegated click listener that upgrades any left-click on a KMS-origin
// link into an SSO hand-off while a member is logged in. Anonymous visitors, and
// links to any other origin, are left completely untouched. Mount once, near the
// app root (inside the auth + settings providers).
export function useKmsSsoLinks(user, settings) {
  useEffect(() => {
    if (!user) return;                       // nobody to hand a session to
    const origin = kmsOrigin(settings);
    if (!origin) return;
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      const a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      let u;
      try { u = new URL(a.href, window.location.origin); } catch { return; }
      if (u.origin !== origin) return;         // not a KMS link
      if (u.pathname === '/sso-login') return; // already an SSO hand-off (e.g. quick link)
      const nextPath = u.pathname + u.search + u.hash;
      const openTab = e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank';
      e.preventDefault();
      kmsSsoGo(origin, nextPath, openTab);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [user, settings]);
}
